import { useEffect, useMemo } from 'react';
import { formatMoney } from '@lombok/shared';
import type { AvailabilityStatus, Product, Sku } from '@lombok/shared';
import { AvailabilityBadge } from '@/components/AvailabilityBadge';
import { SelectField } from '@/components/form';
import { useSkus } from '@/data/hooks';

/** A draft line as the create-order screen tracks it (before validation/submit). */
export interface DraftLine {
  /** Stable local key for React lists (not sent to the API). */
  key: string;
  productId: string;
  skuId: string;
  quantity: number;
}

interface OrderLineRowProps {
  index: number;
  line: DraftLine;
  products: readonly Product[];
  /** Called with a partial patch; the parent merges it into the line. */
  onChange: (patch: Partial<Omit<DraftLine, 'key'>>) => void;
  onRemove: () => void;
  /** Today's availability label of the selected product (customer portal only). */
  availability?: AvailabilityStatus;
  /** Reports the resolved SKU (or undefined) up so the parent can price the cart. */
  onSkuResolved: (key: string, sku: Sku | undefined) => void;
  removable: boolean;
}

/**
 * One editable order line: pick a product → pick one of its SKUs (loaded
 * on-demand) → enter a whole-unit quantity. Shows the line's preview total in
 * USD. Mobile-first: full-width stacked controls, large tap targets.
 */
export function OrderLineRow({
  index,
  line,
  products,
  onChange,
  onRemove,
  availability,
  onSkuResolved,
  removable,
}: OrderLineRowProps) {
  const skusQuery = useSkus(line.productId || undefined);
  const skus = useMemo(() => skusQuery.data ?? [], [skusQuery.data]);

  const productOptions = useMemo(
    () => products.map((p) => ({ value: p.productId, label: p.name })),
    [products],
  );
  const activeSkus = useMemo(() => skus.filter((s) => s.active), [skus]);
  const skuOptions = useMemo(
    () =>
      activeSkus.map((s) => ({
        value: s.skuId,
        label: `${s.code} · ${s.name} (${formatMoney(s.basePriceMinorUsd)})`,
      })),
    [activeSkus],
  );

  const selectedSku = activeSkus.find((s) => s.skuId === line.skuId);

  // Report the resolved SKU to the parent for live cart pricing (effect, never
  // during render). Cleanup clears the entry when this line unmounts.
  useEffect(() => {
    onSkuResolved(line.key, selectedSku);
    return () => onSkuResolved(line.key, undefined);
  }, [line.key, selectedSku, onSkuResolved]);

  const lineTotal = selectedSku ? selectedSku.basePriceMinorUsd * Math.trunc(line.quantity || 0) : 0;

  const skuHint = !line.productId
    ? 'Primero selecciona un producto.'
    : skusQuery.isLoading
      ? 'Cargando presentaciones…'
      : activeSkus.length === 0
        ? 'Este producto no tiene presentaciones activas.'
        : undefined;

  return (
    <div className="flex flex-col gap-3 rounded-md border border-border p-3 sm:p-4">
      <div className="flex items-center justify-between">
        <span className="text-xs font-medium uppercase tracking-wide text-text-muted">
          Línea {index + 1}
        </span>
        {removable ? (
          <button
            type="button"
            onClick={onRemove}
            className="rounded-md border border-border px-2 py-1 text-xs font-medium text-danger transition-colors hover:border-danger hover:bg-surface-muted"
          >
            Quitar
          </button>
        ) : null}
      </div>

      <div className="grid gap-3 sm:grid-cols-2">
        <SelectField
          label="Producto"
          placeholder="Seleccionar producto…"
          options={productOptions}
          value={line.productId}
          onChange={(e) =>
            // Changing the product resets the SKU choice (its SKUs differ).
            onChange({ productId: e.target.value, skuId: '' })
          }
        />
        <SelectField
          label="Presentación (SKU)"
          placeholder="Seleccionar presentación…"
          options={skuOptions}
          value={line.skuId}
          disabled={!line.productId || activeSkus.length === 0}
          hint={skuHint}
          onChange={(e) => onChange({ skuId: e.target.value })}
        />
      </div>

      <div className="grid items-end gap-3 sm:grid-cols-[10rem_1fr]">
        <div>
          <label
            className="mb-1.5 block text-sm font-medium text-text-primary"
            htmlFor={`qty-${line.key}`}
          >
            Cantidad
          </label>
          <input
            id={`qty-${line.key}`}
            type="number"
            inputMode="numeric"
            min={1}
            step={1}
            value={line.quantity || ''}
            onChange={(e) => onChange({ quantity: Math.max(0, Math.trunc(Number(e.target.value))) })}
            className="h-11 w-full rounded-md border border-border-strong bg-surface px-3 text-sm text-text-primary placeholder:text-text-muted focus-visible:border-brand-accent focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
          />
        </div>
        <div className="flex flex-col items-end gap-1 text-right">
          {availability && line.productId ? <AvailabilityBadge status={availability} /> : null}
          <p className="text-xs text-text-muted">Subtotal de línea</p>
          <p className="text-base font-semibold text-text-primary">{formatMoney(lineTotal)}</p>
        </div>
      </div>
    </div>
  );
}
